import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useArticleList } from "../../../api/articlesApi"
import { useCustomerDetail, useCustomerList } from "../../../api/customersApi"
import { useProductList } from "../../../api/productsApi"
import { useSupplierList } from "../../../api/suppliersApi"
import {
  productStatusLabel,
  singleSelectFacet,
} from "../../../constants/masterDataFilters"
import { useFacetOptions } from "../../../hooks/useFacetOptions"
import {
  clearUrlParams,
  stringFilterParam,
} from "../../../hooks/useUrlFilterParams"
import { EntityListRowContent } from "../../shared/EntityListRowContent"
import { RelBadge } from "../../shared/RelBadge"
import { EntityListPanel } from "../EntityListPanel"
import type { MasterDataSortValue } from "../MasterDataSort"
import { useRegisterMasterDataAgentContext } from "../masterDataAgentContext"
import { useDefaultRowSelection } from "../useDefaultRowSelection"
import { useNavSeedSearch } from "../useNavSeedSearch"

interface CustomerListPanelProps {
  selectedIds: Set<string>
  onToggleSelected: (id: string) => void
}

export function CustomerListPanel({
  selectedIds,
  onToggleSelected,
}: CustomerListPanelProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get("id")
  useRegisterMasterDataAgentContext("customer", selectedId)

  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, 300)
  const [sort, setSort] = useState<MasterDataSortValue>("name_asc")

  const [productFilter, setProductFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "product_id",
  )
  const [articleFilter, setArticleFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "article_id",
  )
  const [supplierFilter, setSupplierFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "supplier_id",
  )

  const productFacet = useFacetOptions(useProductList, (p) => ({
    value: p.id,
    label: p.name,
  }))
  const articleFacet = useFacetOptions(useArticleList, (a) => ({
    value: a.id,
    label: a.name,
  }))
  const supplierFacet = useFacetOptions(useSupplierList, (s) => ({
    value: s.id,
    label: s.name,
  }))

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  } = useCustomerList({
    q: debouncedSearch || undefined,
    product_id: productFilter ?? undefined,
    article_id: articleFilter ?? undefined,
    supplier_id: supplierFilter ?? undefined,
    sort,
    limit: 50,
  })

  const rows = data?.pages.flatMap((p) => p.items) ?? []

  useDefaultRowSelection(rows, selectedId, setSearchParams)

  const isNavRedirect = searchParams.get("source") === "nav"
  const { data: seedDetail, isError: seedError } = useCustomerDetail(
    isNavRedirect ? selectedId : null,
  )
  useNavSeedSearch({
    isNavRedirect,
    selectedId,
    detailLoaded: !!seedDetail,
    detailErrored: seedError,
    seedSearchValue: seedDetail ? (seedDetail.name ?? "") : undefined,
    setSearch,
    setSearchParams,
  })

  function selectRow(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("id", id)
      return next
    })
  }

  return (
    <EntityListPanel
      agentTarget="customers.list"
      filterTargetKey="customers.filters"
      rows={rows}
      isLoading={isLoading}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onFetchMore={fetchNextPage}
      selectedId={selectedId}
      onSelectRow={selectRow}
      selectedIds={selectedIds}
      onToggleSelected={onToggleSelected}
      totalCount={data?.pages[0]?.total}
      sort={sort}
      onSortChange={setSort}
      search={search}
      onSearchChange={setSearch}
      searchEntityLabelKey="MC_MD_TAB_CUSTOMERS"
      onClearAllFilters={() =>
        clearUrlParams(setSearchParams, [
          "product_id",
          "article_id",
          "supplier_id",
        ])
      }
      facets={[
        {
          key: "product",
          title: translate("MC_MD_FILTER_PRODUCT_TITLE"),
          ...productFacet,
          ...singleSelectFacet(productFilter, setProductFilter),
        },
        {
          key: "article",
          title: translate("MC_MD_FILTER_ARTICLE_TITLE"),
          ...articleFacet,
          ...singleSelectFacet(articleFilter, setArticleFilter),
        },
        {
          key: "supplier",
          title: translate("MC_MD_FILTER_SUPPLIER_TITLE"),
          ...supplierFacet,
          ...singleSelectFacet(supplierFilter, setSupplierFilter),
        },
      ]}
      renderContent={(row) => (
        <EntityListRowContent
          title={row.name}
          meta={
            (row.customerNumber || row.email) && (
              <>
                {row.customerNumber && (
                  <span className="font-mono">{row.customerNumber}</span>
                )}
                {row.email && <span>{row.email}</span>}
              </>
            )
          }
          badges={
            <>
              <RelBadge
                label={translate("MC_MD_REL_LABEL_PRODUCTS")}
                labelOne={translate("MC_MD_REL_LABEL_PRODUCTS_ONE")}
                count={row.productsSoldCount}
                tone="muted"
                items={row.relProducts.map((p) => ({
                  id: p.id,
                  primary: p.name,
                  secondary: productStatusLabel(p.status, translate),
                }))}
                itemHref={(id) => `../products?id=${id}&source=nav`}
                onShowAll={() => navigate(`../products?customer_id=${row.id}`)}
              />
              <RelBadge
                label={translate("MC_MD_REL_LABEL_ARTICLES")}
                labelOne={translate("MC_MD_REL_LABEL_ARTICLES_ONE")}
                count={row.articlesCount}
                tone="muted"
                items={row.relArticles.map((a) => ({
                  id: a.id,
                  primary: a.articleNumber,
                  secondary: a.name,
                }))}
                itemHref={(id) => `../articles?id=${id}&source=nav`}
                onShowAll={() => navigate(`../articles?customer_id=${row.id}`)}
              />
              <RelBadge
                label={translate("MC_MD_REL_LABEL_SUPPLIERS")}
                labelOne={translate("MC_MD_REL_LABEL_SUPPLIERS_ONE")}
                count={row.suppliersCount}
                tone="muted"
                items={row.relSuppliers.map((s) => ({
                  id: s.id,
                  primary: s.name,
                }))}
                itemHref={(id) => `../suppliers?id=${id}&source=nav`}
                onShowAll={() => navigate(`../suppliers?customer_id=${row.id}`)}
              />
            </>
          }
        />
      )}
    />
  )
}
